import {
  Alert,
  Box,
  Button,
  Checkbox,
  HStack,
  Icon,
  IconButton,
  NativeSelect,
  RadioGroup,
  Separator,
  SimpleGrid,
  Spacer,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useStores } from "../../../shared/stores/RootStore";
import useAutoResizeMap from "../../../hooks/useAutoResizeMap";
import { useEffect } from "react";
import {
  ArrowBackIcon,
  ArrowRightIcon,
  CheckIcon,
  SaveIcon,
} from "../../../shared/views/icons";
import { t } from "i18next";
import { toast } from "../../../shared/views/CustomToaster";
import { HlaWayDetailDelete } from "./HlaWayDetailDelete";
import { MergeIntoMenu } from "./MergeIntoMenu";
import { CrossSectionView } from "./cross-section/CrossSectionView";
import {
  DistanceUnit,
  FacilityType,
  HeavyVehicleFlowType,
} from "@keita/lanepatrol-sdk";
import { MultiSelectionWarning } from "./form/MultiSelectionWarning";
import { NumberField } from "./form/NumberField";
import { FieldWrapper } from "./form/FieldWrapper";
import { CreateOrUpdateHlaLane } from "../../models/HlaLane";
import { HlaLaneForm } from "./form/HlaLaneForm";
import { RadioOptions } from "./form/RadioOptions";
import { NotApplicable } from "./form/NotApplicable";
import { generateOptions } from "./form/utils";
import {
  getDefaultAverageDailyTraffic,
  getDefaultAverageRoadSpeed,
  hlaWayToUpdate,
  HlaWayType,
} from "../../models/HlaWay";
import { NewLaneButton } from "./NewLaneButton";
import turf from "../../../shared/utils/turf";
import { CutHlaModal } from "./cut/CutHlaModal";

const HlaWayDetailBase = () => {
  useAutoResizeMap();
  const { hlaStore, projectStore, filterStore, streetViewStore } = useStores();
  const cutHlaModal = useDisclosure();
  const hlaWay = hlaStore.getSelected()!;
  const project = projectStore.getSelected();
  const canEdit = !!project?.permissions?.canCodeHla;
  const selectedLane = hlaStore.getSelectedLane();
  const form = hlaStore.form;

  useEffect(() => {
    hlaStore.setForm(hlaWayToUpdate(hlaWay));
    return () => {
      if (!hlaStore.changesSaved) {
        submit();
      }
    };
    // eslint-disable-next-line
  }, [hlaWay.id]);

  const submit = () => {
    hlaStore.verify();
    hlaStore
      .saveChanges()
      .then(() => {
        toast("success", `Saved`);
      })
      .catch(() => {
        toast("error", `Failed to save`);
      });
  };

  const laneRequiresRoadFields = (lane: CreateOrUpdateHlaLane | null) => {
    if (!lane) return false;
    const isRoad =
      lane.facilityType === FacilityType.OnRoadBicycleLane ||
      lane.facilityType === FacilityType.RoadShoulder ||
      lane.facilityType === FacilityType.MixedTrafficRoadLane;
    const isAdjacentToRoad =
      (lane.adjacentFeaturesLeft.roadLane != null &&
        !lane.adjacentFeaturesLeft.hardSegregation) ||
      (lane.adjacentFeaturesRight.roadLane != null &&
        !lane.adjacentFeaturesRight.hardSegregation);
    return isRoad || isAdjacentToRoad;
  };

  const roadFieldsRequired =
    laneRequiresRoadFields(form.frontFacingLane) ||
    laneRequiresRoadFields(form.backFacingLane);

  const unverifiedWays = filterStore.filteredHlaWays.filter(
    (way) => !way.verified,
  );

  return (
    <Stack py={2} overflow={"auto"} w={"full"}>
      {/* Header */}
      <HStack px={6}>
        <Box flexShrink={0} width="40px">
          <IconButton
            size="sm"
            variant="ghost"
            onClick={() => {
              hlaStore.clearSelected();
            }}
          >
            <Icon as={ArrowBackIcon} />
          </IconButton>
        </Box>
        <Text px={2} fontWeight="semibold">
          {hlaWay.name ?? "Unknown street"}
        </Text>

        {canEdit && <HlaWayDetailDelete />}
        {canEdit && (
          <Button
            onClick={cutHlaModal.onOpen}
            variant={"plain"}
            size={"sm"}
            colorPalette={"primary"}
            fontSize={"sm"}
          >
            Cut...
          </Button>
        )}
        {canEdit && <MergeIntoMenu />}

        <Spacer />

        <HStack fontSize="sm" gap={4}>
          {canEdit && (
            <Button
              colorPalette={"primary"}
              size={"sm"}
              fontSize="sm"
              onClick={submit}
              loading={hlaStore.savingChanges}
              disabled={hlaStore.changesSaved && form.verified}
            >
              <SaveIcon />{" "}
              {!form.verified && hlaStore.changesSaved ? "Verify" : "Save"}
            </Button>
          )}

          {unverifiedWays.length === 0 ? (
            <HStack fontSize={"xs"} color="gray.500">
              <CheckIcon /> All verified
            </HStack>
          ) : (
            <Button
              colorPalette={"primary"}
              size={"sm"}
              fontSize="sm"
              disabled={!form.verified}
              onClick={async () => {
                const lastPosition =
                  hlaWay.coordinates[hlaWay.coordinates.length - 1];
                if (unverifiedWays.length === 0) {
                  hlaStore.clearSelected();
                  return;
                }

                const waysAndDistanceToLastPoint = unverifiedWays.map(
                  (way) => ({
                    way,
                    distance: turf.distance(lastPosition, way.coordinates[0]),
                  }),
                );
                const sortedWays = waysAndDistanceToLastPoint
                  .toSorted((a, b) => a.distance - b.distance)
                  .map((x) => x.way);

                hlaStore.select(sortedWays[0].id);
                streetViewStore.goToHla(sortedWays[0]);
              }}
            >
              <ArrowRightIcon /> Next
            </Button>
          )}
        </HStack>
      </HStack>

      <MultiSelectionWarning />

      {!canEdit && (
        <Stack px={6}>
          <Alert.Root status={"info"}>
            <Alert.Indicator />
            <Alert.Description>
              You don't have permission to manage high-level assessments in this
              project
            </Alert.Description>
          </Alert.Root>
        </Stack>
      )}

      <Separator />
      <Stack gap={6}>
        <Stack px={6}>
          <Text textStyle={"lg"}>Road</Text>
          <SimpleGrid columns={{ xl: 1, "2xl": 2 }} gap={6}>
            <FieldWrapper label="Type">
              <NativeSelect.Root
                size="sm"
                fontSize={"sm"}
                invalid={form.type == null}
              >
                <NativeSelect.Field
                  value={form.type}
                  onChange={(e) => {
                    const value = e.target.value;
                    hlaStore.code((prev) => ({
                      ...prev,
                      type: value as HlaWayType,
                    }));
                  }}
                >
                  {Object.values(HlaWayType).map((option) => (
                    <option value={option} key={option}>
                      {t(`shared:hlaWayType.${option}`)}
                    </option>
                  ))}
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </FieldWrapper>

            <FieldWrapper label="Heavy vehicle flow">
              {roadFieldsRequired ? (
                <RadioGroup.Root
                  colorPalette={"primary"}
                  disabled={!canEdit}
                  invalid={form.heavyVehicleFlow == null}
                  value={form.heavyVehicleFlow}
                  onValueChange={({ value }) => {
                    hlaStore.code((prev) => ({
                      ...prev,
                      heavyVehicleFlow: value as HeavyVehicleFlowType,
                    }));
                  }}
                >
                  <RadioOptions
                    options={generateOptions(
                      Object.values(HeavyVehicleFlowType),
                    )}
                  />
                </RadioGroup.Root>
              ) : (
                <NotApplicable />
              )}
            </FieldWrapper>

            <FieldWrapper
              label={t("ra:simulatorInputs.speed", {
                unit:
                  projectStore.distanceUnit === DistanceUnit.Imperial
                    ? "mph"
                    : "km/h",
              })}
            >
              <NumberField
                step={10}
                disabled={!canEdit}
                onChange={(value) => {
                  hlaStore.code((prev) => ({
                    ...prev,
                    averageRoadSpeed: value,
                  }));
                }}
                value={form.averageRoadSpeed}
                notApplicable={!roadFieldsRequired}
              />
              {form.averageRoadSpeed == null && (
                <Text fontSize="xs">
                  {`Value defaults to ${getDefaultAverageRoadSpeed(
                    form.type,
                    projectStore.distanceUnit,
                  )} for road type "${t(`shared:hlaWayType.${form.type}`)}"`}
                </Text>
              )}
            </FieldWrapper>

            <FieldWrapper label="Average daily traffic">
              <NumberField
                step={1000}
                disabled={!canEdit}
                onChange={(value) => {
                  hlaStore.code((prev) => ({
                    ...prev,
                    averageDailyTraffic: value,
                  }));
                }}
                value={form.averageDailyTraffic}
                notApplicable={!roadFieldsRequired}
              />
              {form.averageDailyTraffic == null && (
                <Text fontSize="xs">
                  {`Value defaults to ${getDefaultAverageDailyTraffic(
                    form.type,
                  )} for road type "${t(`shared:hlaWayType.${form.type}`)}"`}
                </Text>
              )}
            </FieldWrapper>

            <FieldWrapper label="Street lighting">
              <Checkbox.Root
                disabled={!canEdit}
                colorPalette="primary"
                checked={form.lit}
                onCheckedChange={({ checked }) => {
                  hlaStore.code((prev) => ({
                    ...prev,
                    lit: checked as boolean,
                  }));
                }}
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
              </Checkbox.Root>
            </FieldWrapper>
          </SimpleGrid>
        </Stack>

        <Stack>
          <Separator />
          <Text textStyle={"lg"} px={6}>
            Bike lanes
          </Text>
        </Stack>

        <HStack
          justifyContent={"center"}
          flexDir={projectStore.rightHandTraffic ? "row" : "row-reverse"}
        >
          {form.backFacingLane ? (
            <CrossSectionView
              form={form}
              lane={form.backFacingLane}
              canEdit={canEdit}
            />
          ) : (
            <NewLaneButton frontFacing={false} canEdit={canEdit} />
          )}
          {form.frontFacingLane ? (
            <CrossSectionView
              form={form}
              lane={form.frontFacingLane}
              canEdit={canEdit}
            />
          ) : (
            <NewLaneButton frontFacing={true} canEdit={canEdit} />
          )}
        </HStack>

        {!!selectedLane && <HlaLaneForm lane={selectedLane} />}
      </Stack>

      {cutHlaModal.open && <CutHlaModal onClose={cutHlaModal.onClose} />}
    </Stack>
  );
};

export const HlaWayDetail = observer(HlaWayDetailBase);
